import { useState } from 'react';
import { AppBar, Toolbar, Box, Typography, TextField, InputAdornment, Button, Badge,
         Popover, Stack, Chip, MenuItem, Select, FormControl, InputLabel, IconButton, Tooltip, Divider } from '@mui/material';
import { Search, FilterList, FileDownload, SimCardDownload, CancelPresentation, Cached } from '@mui/icons-material';
import { CONCESSIONARIE, SORTS } from '../lib/parse';
import { wsurl } from '../Common';

const TAGS = ['CIMASA', 'NO CIMASA', 'LEDWALL', 'DIGITALE', 'ILLUMINATO'];

function Logo() {
  return <img src="/logo192.png" width="32" height="32" alt="" aria-hidden="true" style={{ display: 'block' }} />;
}

export default function TopBar({ filters, setFilters, sortKey, setSortKey, selected, onClear, onRefresh, onExportSelected, isMobile }) {
  const [anchor, setAnchor] = useState(null);
  const nFilters = (filters.concessionarie?.length ? 1 : 0) + (filters.votoMin != null ? 1 : 0)
                 + (filters.stato ? 1 : 0) + (filters.tags?.length ? 1 : 0);
  const toggle = (arr = [], v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  return (
    <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: 'white' }}>
      <Toolbar sx={{ gap: 1.5, flexWrap: 'wrap', py: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 1 }}>
          <Logo />
          {!isMobile && <Typography variant="h6" sx={{ color: '#0f172a', letterSpacing: -0.5 }}>Tool Cartelloni</Typography>}
        </Stack>

        <TextField
          size="small" placeholder="Cerca titolo, via, numero o ID…"
          value={filters.search || ''}
          onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          sx={{ flex: 1, minWidth: 160, maxWidth: 520 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />

        <Badge badgeContent={nFilters} color="primary">
          <Button variant="outlined" startIcon={<FilterList />} onClick={(e) => setAnchor(e.currentTarget)}>Filtri</Button>
        </Badge>

        {!isMobile && (
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Ordina per</InputLabel>
            <Select label="Ordina per" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
              {Object.entries(SORTS).map(([k, s]) => <MenuItem key={k} value={k}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        <Tooltip title="Esporta i selezionati in Excel (.xlsx): titolo, voto, indirizzo, Street View, coordinate — nell'ordinamento attivo">
          <span>
            <Button variant="outlined" disabled={!selected.size} startIcon={<SimCardDownload />}
                    onClick={onExportSelected}>
              {isMobile ? '' : `Excel selezionati${selected.size ? ` (${selected.size})` : ''}`}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Esporta tutto il database (CSV)">
          <Button variant="outlined" startIcon={<FileDownload />} onClick={() => window.open(wsurl + '/csv', '_blank')}>
            {isMobile ? '' : 'Esporta tutti'}
          </Button>
        </Tooltip>
        <Tooltip title="Pulisci selezione">
          <span>
            <IconButton color="warning" disabled={!selected.size} onClick={onClear}><CancelPresentation /></IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Aggiorna dal server">
          <IconButton color="primary" onClick={onRefresh}><Cached /></IconButton>
        </Tooltip>

        <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
                 anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
          <Box sx={{ p: 2, width: 340 }}>
            {isMobile && (
              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Ordina per</InputLabel>
                <Select label="Ordina per" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                  {Object.entries(SORTS).map(([k, s]) => <MenuItem key={k} value={k}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <Typography variant="subtitle2" gutterBottom>Concessionaria</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
              {CONCESSIONARIE.map(c => (
                <Chip key={c} label={c} size="small"
                      color={filters.concessionarie?.includes(c) ? 'primary' : 'default'}
                      variant={filters.concessionarie?.includes(c) ? 'filled' : 'outlined'}
                      onClick={() => setFilters(f => ({ ...f, concessionarie: toggle(f.concessionarie, c) }))} />
              ))}
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>Voto minimo</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
              {[null, 4, 6, 7, 8, 9].map(v => (
                <Chip key={String(v)} label={v == null ? 'Tutti' : `V${v}+`} size="small"
                      color={filters.votoMin === v ? 'primary' : 'default'}
                      variant={filters.votoMin === v ? 'filled' : 'outlined'}
                      onClick={() => setFilters(f => ({ ...f, votoMin: v }))} />
              ))}
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>Stato</Typography>
            <Stack direction="row" gap={0.5} sx={{ mb: 1.5 }}>
              {[[null, 'Tutti'], ['revisionato', 'Revisionati'], ['da rivedere', 'Da rivedere']].map(([v, l]) => (
                <Chip key={l} label={l} size="small"
                      color={filters.stato === v ? 'primary' : 'default'}
                      variant={filters.stato === v ? 'filled' : 'outlined'}
                      onClick={() => setFilters(f => ({ ...f, stato: v }))} />
              ))}
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>Caratteristiche</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {TAGS.map(t => (
                <Chip key={t} label={t} size="small"
                      color={filters.tags?.includes(t) ? 'primary' : 'default'}
                      variant={filters.tags?.includes(t) ? 'filled' : 'outlined'}
                      onClick={() => setFilters(f => ({ ...f, tags: toggle(f.tags, t) }))} />
              ))}
            </Stack>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button size="small" onClick={() => setFilters(f => ({ search: f.search, concessionarie: [], votoMin: null, stato: null, tags: [] }))}>
                Azzera filtri
              </Button>
            </Stack>
          </Box>
        </Popover>
      </Toolbar>
    </AppBar>
  );
}
